import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileText } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';

interface LedesFormatDownloadProps {
  invoiceId: number;
  variant?: 'default' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  compact?: boolean; // For table usage - shows only icon
}

type LedesFormat = 'ledes_1998b' | 'ledes_1998bi' | 'ledes_2000' | 'ledes_xml_2_1';

const getFormatLabel = (format: LedesFormat): string => {
  switch (format) {
    case 'ledes_1998b': return 'LEDES 1998B';
    case 'ledes_1998bi': return 'LEDES 1998BI';
    case 'ledes_2000': return 'LEDES 2000';
    case 'ledes_xml_2_1': return 'LEDES XML 2.1';
    default: return 'LEDES Format';
  }
};

export function LedesFormatDownload({ 
  invoiceId, 
  variant = 'outline', 
  size = 'default',
  className = '',
  compact = false 
}: LedesFormatDownloadProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleLedesDownload = async (formatType: LedesFormat) => {
    setIsGenerating(true);
    try {
      const response = await api.get(
        `${ROUTES.API.INVOICING_UNIFIED}${invoiceId}/ledes-format/`,
        {
          params: { format_type: formatType },
          responseType: 'blob',
        }
      );

      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Try to get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `invoice_${invoiceId}_${formatType}.txt`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          fileName = match[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: 'Success',
        description: `${getFormatLabel(formatType)} file downloaded successfully`,
      });
    } catch (error) {
      console.error('LEDES download error:', error);
      toast({
        title: 'Error',
        description: `Failed to download ${getFormatLabel(formatType)} format`,
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          className={className}
          disabled={isGenerating}
          title={compact ? "Download LEDES Format" : undefined}
        >
          <FileText className={compact ? "h-4 w-4" : "h-4 w-4 mr-2"} />
          {!compact && (isGenerating ? 'Generating LEDES...' : 'LEDES Format')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {/* 1998 Formats */}
        <DropdownMenuItem
          onClick={() => handleLedesDownload('ledes_1998b')}
          disabled={isGenerating}
          className="flex flex-col items-start py-3"
        >
          <div className="flex items-center w-full">
            <Download className="h-4 w-4 mr-2" />
            <span className="font-medium">LEDES 1998B</span>
          </div>
          <span className="text-xs text-muted-foreground ml-6">
            US standard pipe-delimited e-billing format
          </span>
        </DropdownMenuItem>
        
        <DropdownMenuItem
          onClick={() => handleLedesDownload('ledes_1998bi')}
          disabled={isGenerating}
          className="flex flex-col items-start py-3"
        >
          <div className="flex items-center w-full">
            <Download className="h-4 w-4 mr-2" />
            <span className="font-medium">LEDES 1998BI</span>
          </div>
          <span className="text-xs text-muted-foreground ml-6">
            LEDES 1998BI with invoice detail
          </span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* 2000+ Formats */}
        <DropdownMenuItem
          onClick={() => handleLedesDownload('ledes_2000')}
          disabled={isGenerating}
          className="flex flex-col items-start py-3"
        >
          <div className="flex items-center w-full">
            <Download className="h-4 w-4 mr-2" />
            <span className="font-medium">LEDES 2000</span>
          </div>
          <span className="text-xs text-muted-foreground ml-6">
            Enhanced LEDES 2000 format
          </span>
        </DropdownMenuItem>
        
        <DropdownMenuItem
          onClick={() => handleLedesDownload('ledes_xml_2_1')}
          disabled={isGenerating}
          className="flex flex-col items-start py-3"
        >
          <div className="flex items-center w-full">
            <Download className="h-4 w-4 mr-2" />
            <span className="font-medium">LEDES XML 2.1</span>
          </div>
          <span className="text-xs text-muted-foreground ml-6">
            XML-based LEDES 2.1 format
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
